<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../../components/Button.svelte';
  import StatusBadge from '../../../components/StatusBadge.svelte';
  import BackButton from '../../../components/BackButton.svelte';
  import EmployeeDonationService, { type DonationRequest } from '../../../lib/api/employee/donation.service';
  import { auth } from '../../../stores/auth.store';

  import { MyEmployerService } from '../../../lib/api/employee/myEmployer.service';
  import ArchiveConfirmModal from '../selectEmployer/ArchiveConfirmModal.svelte';
  import Notification from '../../../components/Notification.svelte';

  export let employerId: string = '';
  
  let loading = false;
  let error: string | null = null;
  let employer: any = null;
  let donations: DonationRequest[] = [];
  let currentEmployerId: string = '';
  let isLoading = false; // Guard to prevent concurrent calls
  let isArchived = false;
  let showArchiveModal = false;
  
  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  async function loadEmployerDetails(id: string) {
    if (!id) {
      error = 'No employer ID provided';
      loading = false;
      isLoading = false;
      return;
    }

    // Prevent duplicate calls and concurrent requests
    if (isLoading || (currentEmployerId === id && employer)) {
      return;
    }

    isLoading = true;
    currentEmployerId = id;
    loading = true;
    error = null;
    
    try {
      const user = auth.getUser();
      if (!user || !user.uuid) {
        if (currentEmployerId === id) {
          error = 'User not authenticated';
          loading = false;
        }
        isLoading = false;
        return;
      }

      const response = await EmployeeDonationService.getEmployerWithDonations(id, user.uuid);
      
      if (response.success && response.data) {
        // Only update if this is still the current request
        if (currentEmployerId === id) {
          employer = response.data.employer;
          donations = response.data.donations || [];
          error = null; // Clear any previous errors
          
          // Check if employer is archived
          await checkArchiveStatus(user.uuid, id);
        }
      } else {
        // Only set error if this is still the current request
        if (currentEmployerId === id) {
          error = response.message || 'Failed to load employer details';
        }
      }
    } catch (err: any) {
      // Only set error if this is still the current request
      if (currentEmployerId === id) {
        console.error('Error loading employer details:', err);
        error = err.message || 'Failed to load employer details';
      }
    } finally {
      if (currentEmployerId === id) {
        loading = false;
      }
      isLoading = false;
    }
  }

  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return EmployeeDonationService.formatDate(dateString);
  }

  function handleDonationClick(donation: DonationRequest) {
    if (donation.uuid) {
      push(`/my-donation/${donation.uuid}`);
    }
  }

  function getStatusClass(status: string): string {
    const statusMap: Record<string, string> = {
      'PAID': 'status-approved',
      'APPROVED': 'status-approved',
      'REJECTED': 'status-rejected',
      'PENDING': 'status-pending',
    };
    return statusMap[status] || 'status-default';
  }

  async function checkArchiveStatus(userUuid: string, employerUuid: string) {
    try {
      // Check archived employers to see if this one is archived
      const archivedResult = await MyEmployerService.getEmployers(userUuid, {
        only_archived: true,
        per_page: 100
      });
      
      if (archivedResult.success && archivedResult.data) {
        isArchived = archivedResult.data.some((emp: any) => emp.uuid === employerUuid);
      }
    } catch (err) {
      console.error('Error checking archive status:', err);
      // Default to false if check fails
      isArchived = false;
    }
  }

  function handleArchiveAction() {
    showArchiveModal = true;
  }

  async function handleArchiveConfirm() {
    const user = auth.getUser();
    if (!user || !user.uuid || !employerId) {
      showNotification('User not authenticated', 'error');
      showArchiveModal = false;
      return;
    }

    try {
      let result;
      
      if (isArchived) {
        // Unarchive the employer
        result = await MyEmployerService.unarchiveEmployer(user.uuid, employerId);
      } else {
        // Archive the employer
        result = await MyEmployerService.archiveEmployer(user.uuid, employerId);
      }
      
      if (result.success) {
        isArchived = !isArchived;
        showNotification(result.message || `Employer ${isArchived ? 'archived' : 'unarchived'} successfully`, 'success');
        // Redirect back to employer list after a short delay
        setTimeout(() => {
          push('/my-employers');
        }, 1500);
      } else {
        showNotification(result.message || `Failed to ${isArchived ? 'unarchive' : 'archive'} employer`, 'error');
      }
    } catch (err: any) {
      showNotification(err.message || `An unexpected error occurred while ${isArchived ? 'unarchiving' : 'archiving'}`, 'error');
    }
    
    showArchiveModal = false;
  }

  function handleArchiveCancel() {
    showArchiveModal = false;
  }

  // Load employer when employerId changes
  $: {
    if (employerId && employerId !== currentEmployerId) {
      // Reset state when employerId changes
      error = null;
      employer = null;
      donations = [];
      loadEmployerDetails(employerId);
    } else if (!employerId && !loading && !employer) {
      // Only set error if we've actually tried to load (i.e., not initial mount)
      // Don't set error immediately on mount if employerId is empty - wait for params
      if (currentEmployerId !== '') {
        error = 'No employer ID provided';
      }
    }
  }

  // Also try to load on mount as fallback
  onMount(() => {
    if (employerId && !employer && !loading) {
      loadEmployerDetails(employerId);
    }
  });
</script>

<div class="employer-detail-page">
  <BackButton text="Back" onClick={() => window.history.back()} />
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading employer details...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={() => loadEmployerDetails(employerId)}>Try Again</Button>
    </div>
  {:else if employer}
    <div class="detail-container">
      <!-- Employer Information Section -->
      <div class="info-section">
        <div class="section-header">
          <h2>Employer Information</h2>
          <button 
            class="archive-button"
            on:click={handleArchiveAction}
            aria-label={isArchived ? 'Unarchive employer' : 'Archive employer'}
          >
            {#if isArchived}
              <!-- Unarchive icon -->
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 12h-8m0 0V4m0 8l7-7m-7 7l7 7"/>
                <circle cx="4" cy="12" r="2"/>
              </svg>
              Unarchive Employer
            {:else}
              <!-- Archive icon -->
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18"></path>
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                <polyline points="10,11 10,17"></polyline>
                <polyline points="14,11 14,17"></polyline>
              </svg>
              Archive Employer
            {/if}
          </button>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Employer Name</span>
            <span class="value-large">{employer.name || 'N/A'}</span>
          </div>
          {#if employer.abn}
            <div class="info-item">
              <span class="info-label">ABN</span>
              <span>{employer.abn}</span>
            </div>
          {/if}
          {#if employer.contact_name}
            <div class="info-item">
              <span class="info-label">Contact Name</span>
              <span>{employer.contact_name}</span>
            </div>
          {/if}
          {#if employer.contact_email}
            <div class="info-item">
              <span class="info-label">Contact Email</span>
              <span>
                <a href="mailto:{employer.contact_email}" class="email-link">
                  {employer.contact_email}
                </a>
              </span>
            </div>
          {/if}
          {#if employer.contact_phone}
            <div class="info-item">
              <span class="info-label">Contact Phone</span>
              <span>
                <a href="tel:{employer.contact_phone}" class="phone-link">
                  {employer.contact_phone}
                </a>
              </span>
            </div>
          {/if}
          {#if employer.head_office_location}
            <div class="info-item">
              <span class="info-label">Head Office Location</span>
              <span>{employer.head_office_location}</span>
            </div>
          {/if}
        </div>
      </div>

      <!-- Linked Donations Section -->
      <div class="info-section">
        <h2>Linked Donations ({donations.length})</h2>
        {#if donations.length > 0}
          <div class="donations-table">
            <table>
              <thead>
                <tr>
                  <th>Charity</th>
                  <th>Hours</th>
                  <th>Value</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {#each donations as donation}
                  <tr on:click={() => handleDonationClick(donation)} role="button" tabindex="0" on:keydown={(e) => e.key === 'Enter' && handleDonationClick(donation)}>
                    <td>
                      {#if donation.charity?.uuid}
                        <button 
                          class="charity-link"
                          on:click|stopPropagation={() => {
                            const charityUuid = donation.charity?.uuid;
                            if (charityUuid) push(`/view-charity/${charityUuid}`);
                          }}
                          aria-label="View charity details"
                        >
                          {donation.charity.name || 'N/A'}
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </button>
                      {:else}
                        {donation.charity?.name || 'N/A'}
                      {/if}
                    </td>
                    <td>{donation.leave_hours} hrs</td>
                    <td>${donation.leave_value?.toFixed(2) || '0.00'}</td>
                    <td>{formatDate(donation.created_at)}</td>
                    <td>
                      <span class="status-badge {getStatusClass(donation.donation_request_status?.name || '')}">
                        {donation.donation_request_status?.name || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <button 
                        class="view-btn" 
                        on:click|stopPropagation={() => handleDonationClick(donation)}
                        aria-label="View donation details"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {:else}
          <div class="no-data-container">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="no-data-icon">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            <h3>No Donations Found</h3>
            <p>You haven't made any donations through this employer yet.</p>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  <!-- Archive Confirmation Modal -->
  <ArchiveConfirmModal 
    bind:isOpen={showArchiveModal}
    employerName={employer?.name || ''}
    isUnarchive={isArchived}
    on:confirm={handleArchiveConfirm}
    on:cancel={handleArchiveCancel}
  />

  <!-- Notification Component -->
  <Notification
    show={notificationShow}
    type={notificationType}
    message={notificationMessage}
    onDismiss={() => { notificationShow = false; }}
  />
</div>

<style>
  .employer-detail-page {
    padding: 2rem;
    max-width: 1000px;
    margin: 0 auto;
    background: #f7fafc;
    min-height: 100vh;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: var(--color-negative, #dc2626);
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .detail-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .info-section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .archive-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #dc2626;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .archive-button:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #b91c1c;
  }

  .archive-button:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }

  .archive-button:active {
    transform: translateY(1px);
  }

  .archive-button svg {
    width: 16px;
    height: 16px;
  }

  .charity-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: none;
    border: none;
    padding: 0;
    color: var(--color-primary, #3b82f6);
    font-size: 0.875rem;
    font-weight: 400;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s;
  }

  .charity-link:hover {
    color: var(--color-primary-hover, #2563eb);
    text-decoration: underline;
  }

  .charity-link:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    border-radius: 4px;
  }

  .charity-link svg {
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.2s, transform 0.2s;
  }

  .charity-link:hover svg {
    opacity: 1;
    transform: translate(2px, -2px);
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-item .info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .info-item span {
    font-size: 1rem;
    color: #111827;
    font-weight: 400;
  }

  .value-large {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
  }

  .email-link,
  .phone-link {
    color: var(--color-primary, #3b82f6);
    text-decoration: none;
    transition: color 0.2s;
  }

  .email-link:hover,
  .phone-link:hover {
    color: var(--color-primary-hover, #2563eb);
    text-decoration: underline;
  }

  .donations-table {
    overflow-x: auto;
  }

  .donations-table table {
    width: 100%;
    border-collapse: collapse;
  }

  .donations-table thead {
    background: #f8fafc;
  }

  .donations-table th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    border-bottom: 1px solid #e5e7eb;
  }

  .donations-table td {
    padding: 1rem;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.875rem;
    color: #111827;
  }

  .donations-table tbody tr {
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .donations-table tbody tr:hover {
    background-color: #f9fafb;
  }

  .donations-table tbody tr:focus {
    outline: 2px solid var(--color-primary, #f0b429);
    outline-offset: -2px;
  }

  .view-btn {
    padding: 0.375rem 0.75rem;
    background: var(--color-primary, #f0b429);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .view-btn:hover {
    background: var(--color-primary-hover, #e0a800);
  }

  .view-btn:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.3);
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .status-approved {
    background: #d1fae5;
    color: #065f46;
  }

  .status-rejected {
    background: #fee2e2;
    color: #991b1b;
  }

  .status-pending {
    background: #fef3c7;
    color: #92400e;
  }

  .status-default {
    background: #f3f4f6;
    color: #4b5563;
  }

  .no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
  }

  .no-data-icon {
    color: #d1d5db;
    margin-bottom: 1.5rem;
  }

  .no-data-container h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .no-data-container p {
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
    font-size: 0.875rem;
  }

  @media (max-width: 768px) {
    .employer-detail-page {
      padding: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .donations-table {
      font-size: 0.75rem;
    }

    .donations-table th,
    .donations-table td {
      padding: 0.5rem;
    }
  }
</style>

